import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  // endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  // endOfYear,
} from "date-fns";

//Function to change date format to yyyy-mm-dd format
export const changeDateFormat = (unformattedDate) => {
  if (!unformattedDate) {
    return "Invalid Date";
  }

  const dateObj = new Date(unformattedDate);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  return format(dateObj, "yyyy-MM-dd");
};

export const humanDateFormat = (unformattedDate) => {
  if (!unformattedDate) {
    return "Invalid Date";
  }

  // Handle ISO week format (e.g., "2025-W35")
  if (typeof unformattedDate === "string" && /^\d{4}-W\d{1,2}$/.test(unformattedDate)) {
    const [year, weekPart] = unformattedDate.split("-W");
    return `Week ${weekPart}, ${year}`;
  }

  // Handle quarter format (e.g., "2025-Q3")
  if (typeof unformattedDate === "string" && /^\d{4}-Q[1-4]$/.test(unformattedDate)) {
    const [year, quarterPart] = unformattedDate.split("-Q");
    return `Q${quarterPart} ${year}`;
  }

  // Handle year-only strings (e.g., "2025") - show as 01-01-YYYY
  if (typeof unformattedDate === "string" && /^\d{4}$/.test(unformattedDate)) {
    return `01-01-${unformattedDate}`;
  }

  const dateObj = new Date(unformattedDate);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  return format(dateObj, "dd-MM-yyyy");
};

export const orderDateTimeGenerator = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const weekFormat = (date) => {
  if (!date) {
    return "Invalid Date";
  }

  // Handle ISO week format (e.g., "2025-W35")
  if (typeof date === "string" && /^\d{4}-W\d{1,2}$/.test(date)) {
    const [year, weekPart] = date.split("-W");
    const weekNumber = parseInt(weekPart);

    // Calculate the date of the first day of the specified week
    const jan1 = new Date(parseInt(year), 0, 1);
    const daysToAdd = (weekNumber - 1) * 7;
    const weekStart = new Date(jan1.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

    // Adjust to get the actual start of the week (Monday)
    const dayOfWeek = weekStart.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(weekStart.getTime() + mondayOffset * 24 * 60 * 60 * 1000);

    const start = startOfWeek(monday);
    const end = endOfWeek(monday);
    return `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`;
  }

  // Handle year-only strings (e.g., "2025") - show first week of year
  if (typeof date === "string" && /^\d{4}$/.test(date)) {
    const yearStart = new Date(parseInt(date), 0, 1); // January 1st of the year
    const start = startOfWeek(yearStart);
    const end = endOfWeek(yearStart);
    return `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`;
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const start = startOfWeek(dateObj);
  const end = endOfWeek(dateObj);
  return `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`;
};

export const monthFormat = (date) => {
  if (!date) {
    return "Invalid Date";
  }

  // Handle year-month strings (e.g., "2025-01" or "2025")
  if (typeof date === "string") {
    if (/^\d{4}$/.test(date)) {
      // Year only - return "Jan YYYY" for the start of the year
      return `Jan ${date}`;
    }
    if (/^\d{4}-\d{2}$/.test(date)) {
      // Year-month format - parse and format
      const [year, month] = date.split("-");
      const dateObj = new Date(parseInt(year), parseInt(month) - 1, 1);
      if (!isNaN(dateObj.getTime())) {
        return `${format(dateObj, "MMM yyyy")}`;
      }
    }
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const start = startOfMonth(dateObj);
  return `${format(start, "MMM yyyy")}`;
};

export const quarterFormat = (date) => {
  if (!date) {
    return "Invalid Date";
  }

  // Handle quarter format (e.g., "2025-Q3")
  if (typeof date === "string" && /^\d{4}-Q[1-4]$/.test(date)) {
    const [year, quarterPart] = date.split("-Q");
    const quarterNumber = parseInt(quarterPart);

    // Calculate start and end months for the quarter
    const startMonth = (quarterNumber - 1) * 3; // Q1=0, Q2=3, Q3=6, Q4=9
    const endMonth = startMonth + 2; // Q1=2, Q2=5, Q3=8, Q4=11

    const start = new Date(parseInt(year), startMonth, 1);
    const end = new Date(parseInt(year), endMonth + 1, 0); // Last day of end month

    return `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`;
  }

  // Handle year-only strings (e.g., "2025") - show full year as quarter
  if (typeof date === "string" && /^\d{4}$/.test(date)) {
    return `Jan 01, ${date} - Dec 31, ${date}`;
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const start = startOfQuarter(dateObj);
  const end = endOfQuarter(dateObj);
  return `${format(start, "MMM dd, yyyy")} - ${format(end, "MMM dd, yyyy")}`;
};

export const yearFormat = (date) => {
  if (!date) {
    return "Invalid Date";
  }

  // Handle year-only strings (e.g., "2025")
  if (typeof date === "string" && /^\d{4}$/.test(date)) {
    return date; // Return the year as-is if it's a 4-digit year string
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const start = startOfYear(dateObj);
  return `${format(start, "yyyy")}`;
};
